/* eslint-disable react/require-default-props */
/**
 * AppAccessTiles component
 *
 * This component displays a list of apps with their corresponding entitlements.
 * It is used to display the apps that will be granted access to the rule.
 */

import React from 'react';
import { ApplicationIcon, Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import './AppAccessTiles.css';

interface AppAccess {
  name: string;
  numEntitlements: number;
  applicationIcon: string;
}

interface AppAccessTilesProps {
  title: string;
  apps: AppAccess[]; // Array of apps to display
  className?: string;
}

/**
 * Component for displaying application access tiles showing which apps will be granted access
 *
 * @param props - The component props
 * @returns JSX element containing the app access tiles
 */
function AppAccessTiles({ title, apps, className }: AppAccessTilesProps) {
  return (
    <Box tag='section' className={cx('Rules-AppAccessTiles', className)}>
      <Box className='Rules-AppAccessTiles-header'>
        <Typography kind='h3' className='Rules-AppAccessTiles-title'>
          {title}
        </Typography>
      </Box>

      <Box className='Rules-AppAccessTiles-grid'>
        {apps.map((app) => (
          <Box key={app.name} className='Rules-AppAccessTiles-tile'>
            <Box className='Rules-AppAccessTiles-iconContainer'>
              <ApplicationIcon
                logo={app.applicationIcon}
                size='medium'
                className='Rules-AppAccessTiles-icon'
              />
            </Box>

            {/* App info */}
            <Box className='Rules-AppAccessTiles-info'>
              <Typography
                kind='body2'
                color='--color-foreground-neutral-bold'
                lineLimit={1}
                tooltipConfigs={{
                  text: app.name,
                  placement: 'top',
                }}
                className='Rules-AppAccessTiles-appName'>
                {app.name}
              </Typography>
              <Typography
                kind='body3'
                className='Rules-AppAccessTiles-entitlements'>
                {app.numEntitlements}
                {app.numEntitlements === 1 ? ' entitlement' : ' entitlements'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default AppAccessTiles;
